import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getPO, getReturn } from '../../api/purchase'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader } from '../../components/PageParts'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import { useVendorNames } from './purchaseShared'
import { MaterialTag, useMaterialIds } from './itemFor'

export default function VendorReturnDetail() {
  const materialIds = useMaterialIds()
  const { id } = useParams()
  const vendorNames = useVendorNames()
  const [hdr, setHdr] = useState(null)
  const [lines, setLines] = useState([])
  const [vendorName, setVendorName] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await getReturn(id)
        if (cancelled) return
        setHdr(data.header || null)
        setLines(data.lines || [])
        if (data?.header?.po_id) {
          const po = await getPO(data.header.po_id).catch(() => null)
          if (!cancelled) setVendorName(String(po?.header?.vendor_name || '').trim())
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load this return.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [id])

  const total = lines.reduce((s, l) => s + num(l.line_total || num(l.quantity) * num(l.unit_cost)), 0)
  const vendor = vendorName || vendorNames[String(hdr?.vendor_id)] || (hdr?.vendor_id ? `Vendor #${hdr.vendor_id}` : '')

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/purchase/vendor-returns" label="Returns" />}
        title={docNo('return', hdr?.return_no) || 'Vendor return'}
        meta={hdr && (
          <>
            {hdr.vendor_id ? <Link to={`/purchase/ap/vendor/${hdr.vendor_id}`}>{vendor}</Link> : vendor}
            {hdr.return_date && ` · returned ${fmtDay(hdr.return_date)}`}
            {hdr.po_id && <> · <Link to={`/purchase/po-detail/${hdr.po_id}`}>{docNo('po', hdr.po_no)}</Link></>}
            {hdr.ref_receipt_id && <> · <Link to={`/purchase/receipts/${hdr.ref_receipt_id}`}>{docNo('receipt', hdr.ref_receipt_no)}</Link></>}
          </>
        )}
      />

      {error && <ErrorBanner message={error} />}

      {loading ? <Spinner label="Loading return…" /> : hdr && (
        <>
          {hdr.notes && <p className="doc-hint">Note: {hdr.notes}</p>}
          <section className="doc-card">
            <div className="doc-card-head">
              <h2>Items sent back</h2>
              <span>{lines.length} {lines.length === 1 ? 'item' : 'items'}</span>
            </div>
            <div className="doc-table-scroll">
              <table className="ci-line-table">
                <thead><tr><th>Item</th><th className="num">Quantity</th><th className="num">Unit cost</th><th className="num">Value</th></tr></thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={`${l.product_id}-${i}`}>
                      <td>{l.name || l.sku}<MaterialTag kind={materialIds.has(Number(l.product_id)) ? 'material' : ''} /><span className="cell-sub">{[l.sku, l.reason_code].filter(Boolean).join(' · ')}</span></td>
                      <td className="num">{num(l.quantity).toLocaleString()}</td>
                      <td className="num">{money(l.unit_cost)}</td>
                      <td className="num ci-total">{money(l.line_total || num(l.quantity) * num(l.unit_cost))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <dl className="doc-totals">
              <div className="is-total"><dt>Total value returned</dt><dd>{money(total)}</dd></div>
            </dl>
          </section>
        </>
      )}
    </div>
  )
}
